import { ANOMALY_BG_COLOR, ANOMALY_SVG_PATH, HYPHEN } from './constants';
import { secureStorage } from './securityUtils';
import DOMPurify from 'dompurify';
import { stateRegionsList } from './stateRegionsList';
import dayjs from 'dayjs';
import { getErrorMessage } from './errorUtils';

const isLoginExpired = () => {
  const currentTime = new Date();
  const loggedInTime = new Date(sessionStorage.getItem('logged-in-time'));
  const timeDifference = currentTime.getTime() - loggedInTime.getTime();
  const hoursDifference = timeDifference / (1000 * 60 * 60);
  return hoursDifference > 24;
};

const getFormattedTimeFromTimestamp = timestamp => {
  if (!timestamp) {
    return '';
  }
  const date = new Date(timestamp);

  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  return date.toLocaleTimeString('en-US', options);
};

const getFormattedDateFromTimestamp = timestamp => {
  const date = new Date(timestamp);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const formatTime = date => {
  if (!date) {
    return '';
  }
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });

  // Combine both formatted date and time
  const formattedDate = timeFormatter.format(new Date(date));
  return formattedDate;
};

const getFormattedDateTimeFromTimestamp = timestamp => {
  if (!timestamp) {
    return HYPHEN;
  }

  const date = new Date(timestamp);

  const optionsDate = {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  };

  const optionsTime = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  const formattedDate = date.toLocaleDateString('en-GB', optionsDate);
  const formattedTime = date.toLocaleTimeString('en-US', optionsTime);

  return `${formattedDate}, ${formattedTime}`;
};

const formatTimeDifference = (timestamp, type = '') => {
  if (!timestamp) {
    return '';
  }
  const now = new Date();
  const givenTime = new Date(timestamp);
  const diffInSeconds = Math.floor((now - givenTime) / 1000);
  if (diffInSeconds < 60) {
    return `${diffInSeconds} secs ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} mins ago`;
  } else if (diffInSeconds === 3600) {
    // only for 1hr return 1hr ago
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hr ago`;
  } else {
    if (type === 'dms') {
      return formatDateTime(givenTime);
    } else {
      const options = { hour: '2-digit', minute: '2-digit' };
      return givenTime.toLocaleTimeString([], options);
    }
  }
};

const formatDateTime = timestamp => {
  if (!timestamp) {
    return '';
  }
  const date = new Date(timestamp);
  const dateFormatter = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Combine both formatted date and time
  const formattedDate = `${dateFormatter.format(date)}, ${timeFormatter.format(date)}`;
  return formattedDate;
};

const formatTimeDifferenceInHours = (startTime, endTime = new Date()) => {
  const formattedEndTime = new Date(endTime);
  const formattedStartTime = new Date(startTime);
  const diffInSeconds = Math.floor(
    (formattedEndTime - formattedStartTime) / 1000
  );
  if (diffInSeconds < 60) {
    return `${diffInSeconds} secs`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} mins`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours > 1 ? 'hrs' : 'hr'}`;
  } else {
    const days = Math.floor(diffInSeconds / 3600);
    return `${days} ${days > 1 ? 'days' : 'day'}`;
  }
};

function kebabToTitleCase(str) {
  return str
    ? str
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
        .join(' ')
    : '';
}

function snakeToTitleCase(str) {
  return str
    ? str
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
        .join(' ')
    : '';
}

const isDateWithinLastXDays = (dateString, days = 180) => {
  const inputDate = new Date(dateString);
  const currentDate = new Date();
  const differenceInTime = currentDate - inputDate;
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);

  return differenceInDays <= days;
};

const getApiUrl = key => {
  const apiLinks = secureStorage.getItem('api-links');
  const parsedApiLinks = apiLinks ? JSON.parse(apiLinks) : {};
  return parsedApiLinks?.[key] || '';
};

const sanitizeInput = input =>
  typeof input === 'string'
    ? DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    : input;

const generateTimestampedFilename = prefix => {
  const now = new Date();
  const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
  const time = now.toTimeString().split(' ')[0].replaceAll(':', ''); // HHMMSS

  return `${prefix}_${date}_${time}.csv`;
};

const createMarker = ({
  backgroundColor = ANOMALY_BG_COLOR.MAJOR,
  iconPath = ANOMALY_SVG_PATH.RASH_DRIVING,
} = {}) => {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg width="77" height="77" viewBox="0 0 77 77" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g filter="url(#filter0_d_6457_184314)">
  <circle opacity="0.5" cx="32" cy="28" r="28" fill="${backgroundColor}"/>
  <circle cx="32" cy="28" r="22" fill="${backgroundColor}"/>
  ${iconPath}  
  </g>
  <defs>
  <filter id="filter0_d_6457_184314" x="0" y="0" width="64" height="64" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset dy="4"/>
  <feGaussianBlur stdDeviation="2"/>
  <feComposite in2="hardAlpha" operator="out"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"/>
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_6457_184314"/>
  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_6457_184314" result="shape"/>
  </filter>
  </defs>
  </svg>
  `)}`;
};

const createDimmedMarker = (originalMarker, backgroundColorOverride) => {
  if (originalMarker?.type === 'svg' && originalMarker?.icon?.url) {
    const markerName = originalMarker.name;
    let dimmedBgColor = '#CCCCCC';
    let iconPath = '';

    switch (markerName) {
      case 'rash-driving':
        iconPath = ANOMALY_SVG_PATH.RASH_DRIVING;
        dimmedBgColor = 'var(--major-anomaly-color-dimmed, #ffdf95 )';
        break;
      case 'driver-drowsiness':
        iconPath = ANOMALY_SVG_PATH.DRIVER_DROWSINESS;
        dimmedBgColor = 'var(--driver-anomaly-color-dimmed, #ffb2b2)';
        break;
      case 'sos-new':
        iconPath = ANOMALY_SVG_PATH.SOS;
        dimmedBgColor = 'var(--sos-ticket-new-dimmed, #ffb2b2)';
        break;
      case 'sos-inprogress':
        iconPath = ANOMALY_SVG_PATH.SOS;
        dimmedBgColor = 'var(--sos-ticket-inprogress-dimmed, #9dc9ff)';
        break;
      case 'sos-resolved':
        iconPath = ANOMALY_SVG_PATH.SOS;
        dimmedBgColor = 'var(--sos-ticket-resolved-dimmed, #5ebd82)';
        break;
      case 'door-open':
        iconPath = ANOMALY_SVG_PATH.DOOR_OPEN;
        dimmedBgColor = 'var(--minor-anomaly-color-dimmed, #A9D8D4)';
        break;
      case 'overspeed':
        iconPath = ANOMALY_SVG_PATH.OVER_SPEEDING;
        dimmedBgColor = 'var(--minor-anomaly-color-dimmed, #A9D8D4)';
        break;
      default:
        return originalMarker.icon;
    }

    const finalBackgroundColor =
      backgroundColorOverride ||
      originalMarker?.hoveredBackgroundColor ||
      dimmedBgColor;

    return {
      ...originalMarker.icon,
      url:
        markerName === 'current-location'
          ? iconPath
          : createMarker({
              backgroundColor: finalBackgroundColor,
              iconPath: iconPath,
            }),
    };
  }

  if (originalMarker?.type === 'circle') {
    return {
      ...originalMarker.icon,
      fillColor: '#CCCCCC',
      strokeColor: '#CCCCCC',
      fillOpacity: 0.6,
      strokeOpacity: 0.6,
    };
  }

  return originalMarker.icon;
};

const getStateOptions = (list = stateRegionsList) => {
  return Object.keys(list)?.map(state => ({
    value: state,
    label: state,
  }));
};

const getCitiesByState = (selectedState, list = stateRegionsList) => {
  if (!selectedState || !list[selectedState]) return [];
  return list[selectedState].map(city => ({
    value: city,
    label: city,
  }));
};

const getCurrentFormattedDate = () => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const now = new Date();
  const day = now.getDate();
  const month = months[now.getMonth()];
  const year = now.getFullYear();

  return `${day} ${month}, ${year}`;
};

const formatHeader = (header, options = {}) => {
  const rawKey = String(header ?? '').trim();

  if (options?.customMappings) {
    if (options.customMappings[rawKey] != null) {
      return options.customMappings[rawKey];
    }
    const match = Object.keys(options.customMappings).find(
      k => k.toLowerCase() === rawKey.toLowerCase()
    );
    if (match) return options.customMappings[match];
  }

  // no auto-formatting — fall back to the raw column key
  return rawKey;
};

const findTimestampColumns = (headers, options = {}) => {
  if (options.timestampColumns) {
    return options.timestampColumns;
  }
  if (options.timestampColumnNames) {
    return options.timestampColumnNames
      .map(name => headers.indexOf(name))
      .filter(index => index !== -1);
  }

  const timestampPatterns = [
    /timestamp/i,
    /created_?at/i,
    /updated_?at/i,
    /date_?time/i,
    /time/i,
    /date/i,
    /_at$/i,
    /_time$/i,
    /_date$/i,
  ];

  return headers
    .map((header, index) =>
      timestampPatterns.some(pattern => pattern.test(header)) ? index : -1
    )
    .filter(index => index !== -1);
};

// asDate: true  -> native Date (Excel date cells, e.g. Reports)
// asDate: false -> formatted string (e.g. Logs)
const formatTimestamps = (
  row,
  timestampColumns = [],
  { asDate = false } = {}
) => {
  const updatedRow = [...row];

  timestampColumns.forEach(colIndex => {
    const value = updatedRow[colIndex];
    if (!value) return;

    if (asDate) {
      const parsed = dayjs(value);
      updatedRow[colIndex] = parsed.isValid() ? parsed.toDate() : value;
    } else {
      updatedRow[colIndex] = getFormattedDateTimeFromTimestamp(value);
    }
  });

  return updatedRow;
};

const getDriverNumber = driverNumber => {
  if (Array.isArray(driverNumber)) {
    return driverNumber.join(', ');
  }

  if (typeof driverNumber === 'string') {
    if (driverNumber.trim() === '') {
      return HYPHEN;
    }

    return driverNumber.replace(/,\s*/g, ', ');
  }

  return driverNumber != null ? String(driverNumber) : HYPHEN;
};

export {
  isLoginExpired,
  getFormattedTimeFromTimestamp,
  getFormattedDateFromTimestamp,
  formatTime,
  getFormattedDateTimeFromTimestamp,
  formatTimeDifference,
  formatTimeDifferenceInHours,
  kebabToTitleCase,
  formatDateTime,
  isDateWithinLastXDays,
  snakeToTitleCase,
  getApiUrl,
  sanitizeInput,
  generateTimestampedFilename,
  createMarker,
  createDimmedMarker,
  getStateOptions,
  getCitiesByState,
  getCurrentFormattedDate,
  formatHeader,
  findTimestampColumns,
  formatTimestamps,
  getDriverNumber,
  getErrorMessage,
};
